import type {
  AuditEvent,
  Catalogue,
  MeAccess,
  Page,
  ProfileHead,
  ProfileReceipt,
  ProfileVersion,
  StaffAccess,
  StaffProposal,
  StaffPreview,
  StaffReceipt,
  UserDetail,
} from './types';

export class ApiError extends Error {
  constructor(public readonly status: number, public readonly code: string) {
    super(code);
  }
}

export class SessionGate {
  private generation = 0;
  private controller = new AbortController();

  rotate(): void {
    this.generation += 1;
    this.controller.abort();
    this.controller = new AbortController();
  }

  snapshot(): {generation: number; signal: AbortSignal} {
    return {generation: this.generation, signal: this.controller.signal};
  }

  accepts(generation: number): boolean {
    return generation === this.generation && !this.controller.signal.aborted;
  }
}

type TokenProvider = () => Promise<string>;

export class StaffApi {
  constructor(private readonly base: string, private readonly token: TokenProvider) {}

  private async request<T>(path: string, init: RequestInit = {}, signal?: AbortSignal): Promise<T> {
    const token = await this.token();
    if (signal?.aborted) throw new DOMException('Request aborted', 'AbortError');
    const response = await fetch(`${this.base}${path}`, {
      ...init,
      signal,
      cache: 'no-store',
      headers: {
        Authorization: `Bearer ${token}`,
        ...(init.body ? {'Content-Type': 'application/json'} : {}),
        ...init.headers,
      },
    });
    if (!response.ok) {
      let code = `http_${response.status}`;
      try {
        const body = await response.json() as {error?: {code?: unknown}};
        if (typeof body.error?.code === 'string') code = body.error.code;
      } catch {
        // Keep the coarse status code; never surface server bodies or tokens.
      }
      throw new ApiError(response.status, code);
    }
    return response.json() as Promise<T>;
  }

  me(signal?: AbortSignal): Promise<MeAccess> { return this.request('/me/access', {}, signal); }
  catalogue(signal?: AbortSignal): Promise<Catalogue> { return this.request('/catalogue', {}, signal); }
  users(cursor?: string, signal?: AbortSignal): Promise<Page<StaffAccess & {id: string}>> {
    return this.request(`/users?limit=25${cursor ? `&cursor=${encodeURIComponent(cursor)}` : ''}`, {}, signal);
  }
  lookup(query: {uid?: string; email?: string}, signal?: AbortSignal): Promise<UserDetail> {
    const params = new URLSearchParams(query as Record<string, string>);
    return this.request(`/users/lookup?${params}`, {}, signal);
  }
  user(uid: string, signal?: AbortSignal): Promise<UserDetail> {
    return this.request(`/users/${encodeURIComponent(uid)}`, {}, signal);
  }
  profiles(cursor?: string, signal?: AbortSignal): Promise<Page<ProfileHead>> {
    return this.request(`/profiles?limit=25${cursor ? `&cursor=${encodeURIComponent(cursor)}` : ''}`, {}, signal);
  }
  profileVersions(profileId: string, cursor?: string, signal?: AbortSignal): Promise<Page<ProfileVersion>> {
    return this.request(`/profiles/${encodeURIComponent(profileId)}/versions?limit=100${cursor ? `&cursor=${encodeURIComponent(cursor)}` : ''}`, {}, signal);
  }
  audit(cursor?: string, signal?: AbortSignal): Promise<Page<AuditEvent>> {
    return this.request(`/audit?limit=25${cursor ? `&cursor=${encodeURIComponent(cursor)}` : ''}`, {}, signal);
  }
  preview(targetUid: string, expectedRevision: number, proposed: StaffProposal, reason: string, signal?: AbortSignal): Promise<StaffPreview> {
    return this.request('/users/preview', {method: 'POST', body: JSON.stringify({targetUid, expectedRevision, proposed, reason})}, signal);
  }
  apply(previewId: string, requestId: string, signal?: AbortSignal): Promise<StaffReceipt> {
    return this.request('/users/apply', {method: 'POST', body: JSON.stringify({previewId, requestId})}, signal);
  }
  createProfile(profile: ProfileVersion, reason: string, requestId: string, signal?: AbortSignal): Promise<ProfileReceipt> {
    return this.request('/profiles', {method: 'POST', body: JSON.stringify({profile, reason, requestId})}, signal);
  }
}

export const messages: Record<string, string> = {
  unauthenticated: 'La session a expiré. Reconnectez-vous.',
  human_identity_required: 'Ce portail exige un compte humain Firebase.',
  reauth_required: 'Authentifiez-vous de nouveau avant cette action sensible.',
  mfa_required: 'Une authentification multifacteur vérifiée est requise.',
  verified_email_required: "L'adresse e-mail du compte doit être vérifiée.",
  not_granted: "Vous n'avez plus la permission nécessaire.",
  staff_suspended: 'Votre accès aux outils migrés est suspendu.',
  revision_conflict: 'Les accès ont changé depuis leur chargement. Les données courantes ont été rechargées.',
  preview_expired: "L'aperçu a expiré. Générez-en un nouveau.",
  idempotency_conflict: "Cette demande existe déjà avec un autre contenu.",
  profile_version_conflict: 'Cette version existe déjà ou ne suit pas la version actuelle.',
  target_disabled: 'Le compte Firebase ciblé est désactivé.',
  last_guardian: 'Cette action retirerait le dernier gestionnaire IAM.',
  last_admin: 'Cette action retirerait le dernier admin.',
  admin_required: 'Seul un admin peut nommer ou modifier un compte admin.',
  admin_not_enabled: 'Les écritures admin ne sont pas encore activées.',
  admin_registry_invalid: 'Le registre des admins est indisponible ou incohérent. Aucun changement n’a été appliqué.',
  no_usable_admin: 'Aucun autre admin ne possède de compte humain actif et vérifié.',
  admin_limit: 'Le nombre maximal d’admins est atteint.',
  self_change: 'La modification de ses propres accès est interdite.',
  actor_permission_missing: 'Vous devez posséder vous-même les droits que vous ajoutez, sur les périmètres concernés.',
  delegation_exceeded: 'Ces droits dépassent votre plafond de délégation.',
  rate_limited: 'Trop de changements récents. Réessayez dans une minute.',
  account_not_found: 'Aucun compte Firebase existant ne correspond exactement.',
  invalid_staff: "La proposition d'accès n'est pas valide.",
  invalid_profile: "La version de profil n'est pas valide.",
  guardian_registry_invalid: 'Le registre des gestionnaires est indisponible. Aucun changement n’a été appliqué.',
  registry_unavailable: 'Le registre des accès est momentanément indisponible.',
};

export function errorMessage(error: unknown): string {
  if (error instanceof ApiError) return messages[error.code] || `Action refusée (${error.code}).`;
  if (error instanceof DOMException && error.name === 'AbortError') return '';
  return error instanceof Error ? error.message : 'Erreur inattendue.';
}
